// CHG-1283 / ITER-3408 — v2 first-visit welcome overlay.
//
// 4-step carousel that fires once per browser via localStorage flags
// (see storage.ts). Brand-aware chrome via useBrand() (CHG-1267).
// Accessibility: aria-modal + aria-labelledby; focus trap on the
// overlay container; Escape and backdrop click dismiss.
//
// The overlay is mounted by WelcomeTrigger (also in this directory),
// which checks isWelcomeNeeded() before rendering. WelcomeTrigger is
// mounted globally in App.tsx so the overlay can fire on any route.

import { useEffect, useRef, useState } from "react";
import { useBrand } from "../../contexts/BrandContext";
import { v1ClassicUrlForHostname } from "../../lib/v1-host-bridge";
import { markCompleted, markDismissed } from "./storage";

interface Step {
  id: string;
  title: string;
  body: string;
}

const STEPS: readonly Step[] = [
  {
    id: "welcome",
    title: "Welcome to the new dashboard",
    body:
      "You're looking at v2 — a focused, faster surface for the same data you've always worked with. v1 stays available; you can switch back any time via the profile menu.",
  },
  {
    id: "productivity",
    title: "Stay productive",
    body:
      "Press ⌘K (or Ctrl+K) anywhere to open the command palette and jump between pages, donors, campaigns. Settings is organized into focused sections you can deep-link to.",
  },
  {
    id: "intelligence",
    title: "Cultural intelligence, in-line",
    body:
      "AI callouts on the Overview page surface ranked suggestions tuned to your org. More AI surfaces (campaign actions, donor notes) roll out per the v2 program.",
  },
  {
    id: "trial",
    title: "Ready when you are",
    body:
      "Start with the Overview to see what's happening today, or switch back to the classic dashboard from your profile menu whenever you want.",
  },
];

interface Props {
  /** Called when the overlay closes (either completed or dismissed).
   * The trigger uses this to flip its internal "show" state so React
   * unmounts the overlay. */
  onClose: () => void;
}

export function WelcomeOverlay({ onClose }: Props) {
  const { chrome } = useBrand();
  const [stepIndex, setStepIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const lastStep = stepIndex === STEPS.length - 1;

  function closeDismissed() {
    markDismissed();
    onClose();
  }

  function closeCompleted() {
    markCompleted();
    onClose();
  }

  function next() {
    if (lastStep) {
      closeCompleted();
    } else {
      setStepIndex((i) => Math.min(i + 1, STEPS.length - 1));
    }
  }

  function back() {
    setStepIndex((i) => Math.max(i - 1, 0));
  }

  // ── Esc to dismiss + focus trap ────────────────────────────────────
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        closeDismissed();
      } else if (e.key === "Tab") {
        const root = containerRef.current;
        if (!root) return;
        const focusables = root.querySelectorAll<HTMLElement>(
          'button, [href], [tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      } else if (e.key === "ArrowRight") {
        next();
      } else if (e.key === "ArrowLeft") {
        back();
      }
    }
    window.addEventListener("keydown", onKey);
    // Initial focus on the dialog's primary action
    const t = window.setTimeout(() => {
      const primary = containerRef.current?.querySelector<HTMLButtonElement>(
        '[data-action="primary"]',
      );
      primary?.focus();
    }, 0);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex]);

  const step = STEPS[stepIndex];
  const v1Url =
    typeof window !== "undefined"
      ? v1ClassicUrlForHostname(window.location.hostname)
      : null;

  return (
    <div
      data-component="welcome-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-overlay-title"
      onClick={(e) => {
        // Backdrop click dismisses; inner card clicks bubble to backdrop
        // only if the user clicks the dimmed area outside the card.
        if (e.target === e.currentTarget) closeDismissed();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(15, 23, 42, 0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        ref={containerRef}
        data-card="welcome"
        style={{
          background: "var(--color-surface)",
          borderRadius: "var(--radius-lg, 12px)",
          boxShadow: "var(--shadow-medium)",
          padding: "28px 28px 20px",
          width: "100%",
          maxWidth: 480,
          maxHeight: "calc(100vh - 48px)",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 16,
          outline: "none",
        }}
      >
        {/* Brand chrome — logo + wordmark */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <img
            src={chrome.logo_url}
            alt={`${chrome.display_name} logo`}
            data-brand-logo
            style={{ width: 36, height: 36, objectFit: "contain" }}
          />
          <div
            data-brand-wordmark
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: "var(--color-text)",
              fontFamily: "var(--font-wordmark, 'Poppins', sans-serif)",
            }}
          >
            {chrome.wordmark}
          </div>
          <button
            type="button"
            onClick={closeDismissed}
            aria-label="Dismiss the welcome overlay"
            data-action="dismiss"
            style={{
              marginLeft: "auto",
              border: "none",
              background: "transparent",
              fontSize: 18,
              lineHeight: 1,
              cursor: "pointer",
              color: "var(--color-text-muted)",
              padding: 4,
            }}
          >
            ×
          </button>
        </div>

        {/* Step content */}
        <div data-step-index={stepIndex} data-step-id={step.id}>
          <h2
            id="welcome-overlay-title"
            style={{
              margin: 0,
              fontSize: 20,
              fontWeight: 600,
              color: "var(--color-text)",
            }}
          >
            {step.title}
          </h2>
          <p
            style={{
              marginTop: 8,
              marginBottom: 0,
              fontSize: 14,
              lineHeight: 1.55,
              color: "var(--color-text)",
            }}
          >
            {step.body}
          </p>
        </div>

        {/* Step indicator */}
        <div
          data-role="step-indicator"
          aria-label={`Step ${stepIndex + 1} of ${STEPS.length}`}
          style={{ display: "flex", gap: 6, alignItems: "center" }}
        >
          {STEPS.map((s, i) => (
            <span
              key={s.id}
              aria-hidden="true"
              style={{
                width: i === stepIndex ? 18 : 8,
                height: 6,
                borderRadius: 3,
                background:
                  i <= stepIndex
                    ? "var(--color-primary, var(--ag-brand-primary, #6366F1))"
                    : "var(--color-border-subtle)",
                transition: "width 120ms ease",
              }}
            />
          ))}
          <span
            style={{
              marginLeft: "auto",
              fontSize: 11,
              color: "var(--color-text-muted)",
            }}
          >
            {stepIndex + 1} / {STEPS.length}
          </span>
        </div>

        {/* Step actions */}
        <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
          {stepIndex > 0 && (
            <button
              type="button"
              onClick={back}
              data-action="back"
              style={secondaryButtonStyle}
            >
              Back
            </button>
          )}
          {/* Step-4 special-cases the secondary CTA to the v1 fallback;
              other steps just show next. */}
          {lastStep && v1Url && (
            <a
              href={v1Url}
              data-action="use-classic"
              onClick={closeDismissed}
              style={{
                ...secondaryButtonStyle,
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              Use classic for now
            </a>
          )}
          <button
            type="button"
            onClick={next}
            data-action="primary"
            style={{ ...primaryButtonStyle, marginLeft: "auto" }}
          >
            {lastStep ? "Go to the dashboard" : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
}

const primaryButtonStyle: React.CSSProperties = {
  padding: "8px 16px",
  fontSize: 14,
  fontWeight: 500,
  border: "none",
  borderRadius: "var(--radius-md, 8px)",
  background: "var(--color-primary, var(--ag-brand-primary, #6366F1))",
  color: "var(--color-on-primary, #fff)",
  cursor: "pointer",
};

const secondaryButtonStyle: React.CSSProperties = {
  padding: "8px 14px",
  fontSize: 14,
  fontWeight: 500,
  border: "1px solid var(--color-border)",
  borderRadius: "var(--radius-md, 8px)",
  background: "transparent",
  color: "var(--color-text)",
  cursor: "pointer",
  fontFamily: "inherit",
};
